import { Ref } from 'react';
import { Box, GlobalStyles } from '@mui/material';
import '@openfin/core-web/styles.css';
import { SetupSmartBarFocusHotkey } from '#features/app/components/web/SetupSmartBarFocusHotkey';
import { ConnectivityDialog } from './ConnectivityDialog';
import { Header } from './Header';
import { useWebPlatformDimensions } from './useWebPlatformDimensions';

type WebPlatformProps = {
  platformContainerRef: Ref<HTMLDivElement>;
};

const containerPadding = 2;

export const WebPlatform = (props: WebPlatformProps) => {
  const { platformContainerRef } = props;
  const { headerHeight, footerHeight } = useWebPlatformDimensions();

  return (
    <>
      {/* Setup hotkey listener - wrapped in withSubscribe so it doesn't suspend the platform while waiting for global preferences */}
      <SetupSmartBarFocusHotkey />
      {/* revert global styles introduced by openfin core-web css import */}
      <GlobalStyles
        styles={(theme) => ({
          body: {
            '--body-font-color': theme.ifx.palette['Colors/Text/text-primary'],
            '--main-background-color':
              theme.ifx.palette['Colors/Background/bg-primary'],
            '--drop-target-border-color':
              theme.ifx.palette['Colors/Border/border-brand'],
            '--tab-border-top-color': 'transparent', // note the variable needs to be defined here, as the dragged tab is rendered outside of the container
          },
        })}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <ConnectivityDialog />
        <Header />
        <Box
          sx={(theme) => ({
            p: containerPadding,
            backgroundColor: theme.ifx.palette['Colors/Background/bg-primary'],
          })}
        >
          <Box
            ref={platformContainerRef}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: `calc(100vh - ${headerHeight + footerHeight + 2 * containerPadding}px)`,
              '--frame-background-color':
                theme.ifx.palette['Colors/Background/bg-primary_alt'],
              '--tabs-bar-background-color':
                theme.ifx.palette['Colors/Background/bg-primary'],
              '--tab-font-color':
                theme.ifx.palette['Colors/Text/text-secondary'],
              '--tab-background-color':
                theme.ifx.palette['Colors/Background/bg-primary'],
              '--tab-background-active-color':
                theme.ifx.palette['Colors/Background/bg-primary_alt'],
              '--tab-border-top-highlight-color': 'transparent',
              '--splitter-color':
                theme.ifx.palette['Colors/Border/border-secondary'],
              '--corner-radius': `${theme.ifx.radius['radius-xs']}px`,
              '.lm_header': {
                px: '8px',
                borderColor: 'transparent',
              },
              '.lm_header .lm_tabs .lm_tab .lm_title': {
                width: 'fit-content',
                paddingRight: '8px',
              },
              '.lm_header .lm_tabs .lm_tab, .lm_header .lm_tabs .lm_tab:hover':
                {
                  opacity: 1,
                },

              '.lm_header .lm_tab.lm_active': {
                '--tab-font-color':
                  theme.ifx.palette['Colors/Text/text-primary'],
              },

              '.lm_splitter:not(:hover)': {
                opacity: 0.5,
              },
            })}
          >
            {/* container for openfin web layout */}
          </Box>
        </Box>
      </Box>
    </>
  );
};
